import { useState } from 'react';
import Link from '@eliadmin/core/src/components/link';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
} from '@eliadmin/core/src/components/ui/responsive-dialog';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';
import { RadioGroup, RadioGroupItem } from '@eliadmin/core/src/components/ui/radio-group';
import { Checkbox } from '@eliadmin/core/src/components/ui/checkbox';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { JobportalPaymentCard } from '../jobportal/jobportal-payment-card';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import type { CartPayment as CartPaymentType } from '@eliadmin/core/src/api/cart/cart-types';
import React from 'react';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { fetchSlugDetail } from '@eliadmin/core/src/api/slugs/slugs-api';
import { Label } from '@eliadmin/core/src/components/ui/label';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { trackEcommerce } from '../analytics/analytics-helper';
import { payCustomSubscription } from '@eliadmin/core/src/api/jobportal/orders-api';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import {
  getJobPortalPaymentMethods,
  keepJobPortalPaymentSelection,
} from '../../utils/jobportal-payment-methods';

export const CurriculumAccessPaymentDialog = ({
  open,
  setOpen,
  payments,
}: {
  open: boolean;
  setOpen: (isOpen: boolean) => void;
  payments?: CartPaymentType[];
}) => {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const currency = useGlobalStore(s => s.generalLayout.currency);
  const [selectedPayment, setSelectedPayment] = useState<string>(
    payments?.[0]?.id?.toString() || '',
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [gdprAccepted, setGdprAccepted] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<string>('');
  const { me, isInitialized } = useCustomerStore();

  // Fetch curriculum product with id 10 using slug
  const { data: product, isLoading: isProductLoading } = useQuery({
    queryKey: ['curriculum-product-3', locale, 13, me?.authToken ?? null],
    queryFn: async () => {
      const slugRes = await fetchProducts({
        locale,
        authToken: me?.authToken,
        query: { where: { id: { in: [13] } } },
      }).then((r: any) => r.data[0]?.slug);

      if (!slugRes) return;
      const detail = await fetchSlugDetail({
        locale,
        slug: slugRes,
        authToken: me?.authToken,
        cache: 'no-store',
      });
      return detail['App\\Models\\Product'];
    },
    enabled: open && isInitialized && !!me?.authToken,
  });

  // Fix type for variants
  const variants =
    product?.variants?.filter((variant: any) => {
      const priceDisplay = getProductPriceDisplay(variant);
      return priceDisplay.hasPrice && !priceDisplay.priceOnRequest;
    }) || [];

  // Set default selected variant
  React.useEffect(() => {
    if (variants.length > 0 && !selectedVariant) {
      setSelectedVariant(String(variants[0].id));
    }
  }, [variants, selectedVariant]);

  const selectedVariantData = variants.find(
    (variant: any) => String(variant.id) === selectedVariant,
  );
  const total = selectedVariantData
    ? (getProductPriceDisplay(selectedVariantData).current ?? NaN)
    : NaN;
  const availablePayments = getJobPortalPaymentMethods(payments, total);
  const hasSelectedPayment = availablePayments.some(
    payment => String(payment.id) === selectedPayment,
  );
  React.useEffect(() => {
    setSelectedPayment(current =>
      keepJobPortalPaymentSelection(getJobPortalPaymentMethods(payments, total), current),
    );
  }, [payments, total]);

  const handleBuy = async () => {
    if (
      !selectedVariantData ||
      !me?.authToken ||
      !hasSelectedPayment ||
      !gdprAccepted ||
      isSubmitting ||
      !open
    ) {
      console.log('Missing required data for purchase');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await payCustomSubscription({
        authToken: me.authToken,
        body: {
          variants: [{ id: Number(selectedVariant) }],
          payment_type: { id: Number(selectedPayment) },
          subscription_type: { id: 3 },
          return_url: process.env.NEXT_PUBLIC_APP_URL ?? 'https://www.job-interview.sk',
        },
      });
      try {
        const transaction_id = `curriculum-access-${selectedVariant}-${Date.now()}`;
        const variantObj = variants.find((v: any) => String(v?.id) === String(selectedVariant));
        const priceWithVat = variantObj ? (getProductPriceDisplay(variantObj).current ?? 0) : 0;
        trackEcommerce.purchase({
          identifier: transaction_id,
          price: priceWithVat,
          currency,
          price_without_vat: priceWithVat,
          shipment_price_with_vat: 0,
          items: [
            {
              item_id: `curriculum-${selectedVariant}`,
              item_name: 'Prístup k životopisom',
              item_brand: 'job-interview',
              price: priceWithVat,
              item_category: 'curriculums',
              quantity: 1,
              google_business_vertical: 'retail',
              id: `curriculum-${selectedVariant}`,
            },
          ],
          affiliation: '',
          coupon: '',
        });
      } catch {
        // ignore analytics errors
      }
      if (!response?.redirect?.redirect.includes(process.env.NEXT_PUBLIC_APP_URL)) {
        window.location.href = response?.redirect?.redirect;
      } else {
        setOpen(false);
        window.location.reload();
      }
    } catch (error) {
      console.error('Error paying for curriculum access:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={setOpen}>
      <ResponsiveDialogContent>
        {isSubmitting || isProductLoading ? (
          <div className="flex min-h-[120px] items-center justify-center">
            <Spinner className="h-16 w-16" />
          </div>
        ) : (
          <>
            <ResponsiveDialogHeader>
              <div className="mb-4 border-b border-gray-200 pb-2 text-center text-2xl font-extrabold tracking-tight text-primary">
                {t('text3481')}
              </div>
            </ResponsiveDialogHeader>
            <div className="mb-4">
              <RadioGroup value={selectedVariant} onValueChange={setSelectedVariant}>
                {variants.map((variant: any) => (
                  <Label
                    key={variant.id}
                    htmlFor={`curriculum-variant-${variant.id}`}
                    className="mb-2 flex cursor-pointer items-center justify-between rounded-lg border p-4"
                  >
                    <div className="flex items-center gap-4">
                      <RadioGroupItem
                        id={`curriculum-variant-${variant.id}`}
                        value={String(variant.id)}
                        className="job-interview-payment-radio"
                      />
                      <div>
                        <div className="text-sm font-bold">{t('text3482')}</div>
                        <div className="text-xs font-semibold text-gray-500">{variant.name}</div>
                        {variant.description && (
                          <HtmlRenderer html={variant.description} className="[&_p]:text-xs" />
                        )}
                      </div>
                    </div>
                    <span className="text-sm font-bold">
                      {formatPrice(getProductPriceDisplay(variant).current ?? 0, currency)}{' '}
                      {t('text443')}
                    </span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
            <div className="mb-1 text-base font-semibold">{t('text124')}</div>
            <RadioGroup value={selectedPayment} onValueChange={setSelectedPayment} className="mb-4">
              {availablePayments.map(payment => (
                <JobportalPaymentCard key={payment.id} payment={payment} />
              ))}
            </RadioGroup>
            {selectedVariantData && availablePayments.length === 0 && (
              <p role="alert" className="mb-4 text-sm text-destructive">
                {t('Pre túto objednávku nie je dostupný spôsob platby. Kontaktujte podporu.')}
              </p>
            )}
            <div className="mb-6 flex items-start gap-2">
              <Checkbox
                checked={gdprAccepted}
                onCheckedChange={checked => setGdprAccepted(!!checked.valueOf())}
                name="gdpr"
              />
              <label htmlFor="gdpr" className="text-sm">
                {t('text613')}{' '}
                <Link href="/gdpr" className="text-primary underline" prefetch={false}>
                  {t('text614')}
                </Link>{' '}
                a pravidlami používania stránky.
              </label>
            </div>
            <div className="flex w-full justify-between">
              <Button variant="default-alt" type="button" onClick={() => setOpen(false)}>
                {t('text377')}
              </Button>
              <Button
                onClick={handleBuy}
                type="button"
                disabled={
                  !gdprAccepted || !selectedVariantData || !hasSelectedPayment || isSubmitting
                }
              >
                {t('text2502')}
              </Button>
            </div>
          </>
        )}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};
